import { getNonDeletedElements } from "@excalidraw/element";
import { mutateElement } from "@excalidraw/element";
import { newFrameElement } from "@excalidraw/element";
import { isFrameLikeElement } from "@excalidraw/element";
import {
  addElementsToFrame,
  removeAllElementsFromFrame,
} from "@excalidraw/element";
import { getFrameChildren } from "@excalidraw/element";
import { reapplyAutoLayoutToAllFrames } from "@excalidraw/element";

import { KEYS, updateActiveTool, FRAME_STYLE } from "@excalidraw/common";

import { getElementsInGroup } from "@excalidraw/element";

import { getCommonBounds } from "@excalidraw/element";

import { CaptureUpdateAction } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { setCursorForShape } from "../cursor";
import { frameToolIcon } from "../components/icons";
import { getSelectedElements } from "../scene";

import { register } from "./register";

import type { AppClassProperties, AppState, UIAppState } from "../types";

const isSingleFrameSelected = (
  appState: UIAppState,
  app: AppClassProperties,
) => {
  const selectedElements = app.scene.getSelectedElements(appState);

  return (
    selectedElements.length === 1 && isFrameLikeElement(selectedElements[0])
  );
};

export const actionSelectAllElementsInFrame = register({
  name: "selectAllElementsInFrame",
  label: "labels.selectAllElementsInFrame",
  trackEvent: { category: "canvas" },
  perform: (elements, appState, _, app) => {
    const selectedElement =
      app.scene.getSelectedElements(appState).at(0) || null;

    if (isFrameLikeElement(selectedElement)) {
      const elementsInFrame = getFrameChildren(
        getNonDeletedElements(elements),
        selectedElement.id,
      ).filter((element) => !(element.type === "text" && element.containerId));

      return {
        elements,
        appState: {
          ...appState,
          selectedElementIds: elementsInFrame.reduce((acc, element) => {
            acc[element.id] = true;
            return acc;
          }, {} as Record<ExcalidrawElement["id"], true>),
        },
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      };
    }

    return {
      elements,
      appState,
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
  predicate: (elements, appState, _, app) =>
    isSingleFrameSelected(appState, app),
});

export const actionRemoveAllElementsFromFrame = register({
  name: "removeAllElementsFromFrame",
  label: "labels.removeAllElementsFromFrame",
  trackEvent: { category: "history" },
  perform: (elements, appState, _, app) => {
    const selectedElement =
      app.scene.getSelectedElements(appState).at(0) || null;

    if (isFrameLikeElement(selectedElement)) {
      return {
        elements: removeAllElementsFromFrame(elements, selectedElement),
        appState: {
          ...appState,
          selectedElementIds: {
            [selectedElement.id]: true,
          },
        },
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      };
    }

    return {
      elements,
      appState,
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
  predicate: (elements, appState, _, app) =>
    isSingleFrameSelected(appState, app),
});

export const actionupdateFrameRendering = register({
  name: "updateFrameRendering",
  label: "labels.updateFrameRendering",
  viewMode: true,
  trackEvent: { category: "canvas" },
  perform: (elements, appState) => {
    return {
      elements,
      appState: {
        ...appState,
        frameRendering: {
          ...appState.frameRendering,
          enabled: !appState.frameRendering.enabled,
        },
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
  checked: (appState: AppState) => appState.frameRendering.enabled,
});

export const actionSetFrameAsActiveTool = register({
  name: "setFrameAsActiveTool",
  label: "toolBar.frame",
  trackEvent: { category: "toolbar" },
  icon: frameToolIcon,
  viewMode: false,
  perform: (elements, appState, _, app) => {
    const nextActiveTool = updateActiveTool(appState, {
      type: "frame",
    });

    setCursorForShape(app.interactiveCanvas, {
      ...appState,
      activeTool: nextActiveTool,
    });

    return {
      elements,
      appState: {
        ...appState,
        activeTool: updateActiveTool(appState, {
          type: "frame",
        }),
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
  keyTest: (event) =>
    !event[KEYS.CTRL_OR_CMD] &&
    !event.shiftKey &&
    !event.altKey &&
    event.key.toLocaleLowerCase() === KEYS.F,
});

export const actionWrapSelectionInFrame = register({
  name: "wrapSelectionInFrame",
  label: "labels.wrapSelectionInFrame",
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) => {
    const selectedElements = getSelectedElements(elements, appState);

    // Show action as long as at least one element is selected, even if frames
    // are part of the selection. This enables wrapping existing frames and
    // other elements into a new parent frame (nested frames are supported).
    return selectedElements.length > 0;
  },
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] && event.altKey && event.code === "KeyG",
  perform: (elements, appState, _, app) => {
    const selectedElements = getSelectedElements(elements, appState);
    const elementsMap = app.scene.getNonDeletedElementsMap();

    const [x1, y1, x2, y2] = getCommonBounds(selectedElements, elementsMap);
    const PADDING = 1; // 1px padding for frame wrapping

    // Check if selected elements are inside a frame to maintain nesting
    const parentFrameId =
      selectedElements.find((el) => el.frameId)?.frameId || null;

    // Use the same styling as the Frame tool
    const {
      backgroundColor: _bg,
      strokeColor: _stroke,
      roundness: _round,
      ...frameStyleWithoutBgStrokeAndRoundness
    } = FRAME_STYLE;

    const frame = newFrameElement({
      x: x1 - PADDING,
      y: y1 - PADDING,
      width: x2 - x1 + PADDING * 2,
      height: y2 - y1 + PADDING * 2,
      backgroundColor: "transparent",
      strokeColor: "transparent", // Transparent border for clean appearance
      roundness: null,
      frameId: parentFrameId, // Maintain nesting by setting parent frame
      ...frameStyleWithoutBgStrokeAndRoundness,
    });

    // for a selected partial group, we want to remove it from the remainder of the group
    if (appState.editingGroupId) {
      const elementsInGroup = getElementsInGroup(
        selectedElements,
        appState.editingGroupId,
      );

      for (const elementInGroup of elementsInGroup) {
        const index = elementInGroup.groupIds.indexOf(appState.editingGroupId);

        mutateElement(elementInGroup, elementsMap, {
          groupIds: elementInGroup.groupIds.slice(0, index),
        });
      }
    }

    // If the frame is nested, hide its header like we do for dragged frames
    if (parentFrameId) {
      (frame as any).showName = false;
    }

    // Insert the frame at the correct position in the document order
    // to maintain proper auto layout sequence
    const allElements = app.scene.getElementsIncludingDeleted();
    const firstSelectedElement = selectedElements[0];
    const insertionIndex = allElements.findIndex(
      (el) => el.id === firstSelectedElement.id,
    );

    // Insert the frame just before the first selected element
    const elementsWithFrame = [
      ...allElements.slice(0, insertionIndex),
      frame,
      ...allElements.slice(insertionIndex),
    ];

    const nextElements = addElementsToFrame(
      elementsWithFrame,
      selectedElements,
      frame,
      appState,
    );

    // If the parent frame has auto layout enabled, trigger rerendering
    if (parentFrameId) {
      const parentFrame = app.scene
        .getNonDeletedElementsMap()
        .get(parentFrameId);
      if (
        parentFrame &&
        parentFrame.type === "frame" &&
        (parentFrame as any).autoLayout?.enabled
      ) {
        // Use the same layered rerendering approach as other auto layout operations
        setTimeout(() => {
          const allElements = app.scene.getElementsIncludingDeleted();
          const elementsMap = app.scene.getNonDeletedElementsMap();
          const autoLayoutElements = reapplyAutoLayoutToAllFrames(
            allElements,
            elementsMap,
          );
          if (autoLayoutElements !== allElements) {
            app.scene.replaceAllElements(autoLayoutElements);

            // Trigger additional auto layout rerendering
            setTimeout(() => {
              const currentElements = app.scene.getElementsIncludingDeleted();
              const currentElementsMap = app.scene.getNonDeletedElementsMap();
              const rerenderedElements = reapplyAutoLayoutToAllFrames(
                currentElements,
                currentElementsMap,
              );
              if (rerenderedElements !== currentElements) {
                app.scene.replaceAllElements(rerenderedElements);
              }

              // Final trigger to ensure visual re-rendering
              app.scene.triggerUpdate();
            }, 0);
          } else {
            // Even if no layout changes, trigger update for visual feedback
            app.scene.triggerUpdate();
          }
        }, 0);
      }
    }

    return {
      elements: nextElements,
      appState: {
        selectedElementIds: { [frame.id]: true },
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
});

// Note: For now, we'll implement the select layer functionality
// directly in the context menu rather than as a separate action
// This is a placeholder for future implementation
